'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Icon, IconSprite } from './ResiboIcons'
import {
  CATEGORIES,
  CURRENCIES,
  CURRENCY_NAMES,
  EXTENSION_TYPES,
  MAX_FILE_BYTES,
  MAX_RECORDS,
  MAX_SESSION_BYTES,
  SUPPORTED_TYPES,
  TAX_LABELS,
  answerQuery,
  cents,
  cloneSamples,
  decimal,
  issues,
  longDate,
  money,
  ready,
  safeCsv,
  sampleData,
  shortDate,
  sourceCopies,
  type LineItem,
  type Receipt,
} from './resibo-data'

type Filter = 'all' | 'review' | 'ready'
type DialogName = 'edit' | 'help' | 'ask' | 'source' | 'reset' | null

type Draft = {
  merchant: string
  date: string
  category: string
  currency: string
  total: string
  tax: string
  taxLabel: string
  items: Array<{ name: string; qty: string; price: string }>
}

/* ------------------------------------------------------------------ dialog */

function Dialog({
  open,
  onClose,
  className = '',
  labelledBy,
  children,
}: {
  open: boolean
  onClose: () => void
  className?: string
  labelledBy: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      className={`rsb-dialog ${className}`.trim()}
      aria-labelledby={labelledBy}
      onClose={onClose}
      onCancel={onClose}
    >
      {open ? children : null}
    </dialog>
  )
}

function DialogHead({
  id,
  title,
  closeLabel,
  onClose,
}: {
  id: string
  title: string
  closeLabel: string
  onClose: () => void
}) {
  return (
    <div className='rsb-dialog-head'>
      <h2 id={id}>{title}</h2>
      <button
        type='button'
        className='rsb-icon-btn'
        aria-label={closeLabel}
        onClick={onClose}
      >
        <Icon name='close' />
      </button>
    </div>
  )
}

/* -------------------------------------------------------------------- page */

export default function ResiboPage() {
  const [records, setRecords] = useState<Receipt[]>(cloneSamples)
  const [selectedId, setSelectedId] = useState<string | null>(
    sampleData[0]?.id ?? null
  )
  const [filter, setFilter] = useState<Filter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [currencyScope, setCurrencyScope] = useState('PHP')
  const [dialog, setDialog] = useState<DialogName>(null)
  const [dragging, setDragging] = useState(false)
  const [toast, setToast] = useState<{ message: string; undo: boolean } | null>(
    null
  )
  const [undoRecord, setUndoRecord] = useState<{
    record: Receipt
    index: number
  } | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [queryAnswer, setQueryAnswer] = useState<string | null>(null)
  const [brokenPreviews, setBrokenPreviews] = useState<string[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const workspaceRef = useRef<HTMLElement>(null)
  const dragDepth = useRef(0)
  const fileCounter = useRef(0)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* --------------------------------------------------------------- derived */

  const search = searchInput.toLowerCase().trim()

  const currencies = useMemo(
    () => [...new Set(records.map(r => r.currency))],
    [records]
  )
  const scope = currencies.includes(currencyScope)
    ? currencyScope
    : (currencies[0] ?? 'PHP')

  const sessionTotal = useMemo(
    () =>
      records
        .filter(r => r.currency === scope)
        .reduce((n, r) => n + cents(r.total || 0), 0) / 100,
    [records, scope]
  )

  const readyCount = useMemo(
    () => records.filter(r => ready(r)).length,
    [records]
  )
  const reviewCount = records.length - readyCount

  const visible = useMemo(
    () =>
      records.filter(
        r =>
          (filter === 'all' || (filter === 'ready' ? ready(r) : !ready(r))) &&
          `${r.merchant} ${r.filename} ${r.category} ${r.currency}`
            .toLowerCase()
            .includes(search)
      ),
    [records, filter, search]
  )

  const activeId = visible.some(r => r.id === selectedId)
    ? selectedId
    : (visible[0]?.id ?? null)
  const selected = records.find(r => r.id === activeId) ?? null

  const eligible = visible.filter(r => ready(r))
  const samples = records.filter(r => r.sample).length

  const datasetLabel =
    records.length === 0
      ? 'EMPTY SESSION'
      : samples === records.length
        ? 'SAMPLE DATA'
        : samples
          ? 'SAMPLES + YOUR FILES'
          : 'YOUR FILES'

  const overviewNote =
    samples === records.length && samples
      ? `Explore ${samples} sample receipts.`
      : `${records.length} receipt${records.length === 1 ? '' : 's'} in this session.`

  /* ---------------------------------------------------------------- toasts */

  const showToast = useCallback((message: string, undo = false) => {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToast({ message, undo })
    toastTimer.current = setTimeout(() => setToast(null), undo ? 10000 : 6500)
  }, [])

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current)
    },
    []
  )

  /* ------------------------------------------------- unsaved-session guard */

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (records.some(r => !r.sample)) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [records])

  /* A page-wide drop must not navigate away from the workspace. */
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault()
    document.addEventListener('dragover', prevent)
    document.addEventListener('drop', prevent)
    return () => {
      document.removeEventListener('dragover', prevent)
      document.removeEventListener('drop', prevent)
    }
  }, [])

  /* ----------------------------------------------------------- file intake */

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      // Built outside the state updater on purpose: this allocates object URLs
      // and counts errors, and React may invoke an updater more than once.
      const created: Receipt[] = []
      const errors: string[] = []
      // The pending undo record is still restorable, so it holds a slot and its
      // bytes. Counting it here is what keeps Undo from ever overrunning a cap.
      const pendingUndo = undoRecord ? 1 : 0
      let bytes =
        records.reduce((sum, r) => sum + (r.size || 0), 0) +
        (undoRecord?.record.size || 0)

      for (const f of Array.from(files)) {
        const type =
          f.type ||
          EXTENSION_TYPES[f.name.split('.').pop()?.toLowerCase() ?? '']
        if (!SUPPORTED_TYPES[type]) {
          errors.push(`${f.name}: use JPG, PNG, WEBP or PDF.`)
          continue
        }
        if (!f.size) {
          errors.push(`${f.name}: the file is empty.`)
          continue
        }
        if (f.size > MAX_FILE_BYTES) {
          errors.push(`${f.name}: larger than 10 MB.`)
          continue
        }
        if (
          records.length + created.length + pendingUndo >= MAX_RECORDS ||
          bytes + f.size > MAX_SESSION_BYTES
        ) {
          errors.push(
            'Session limit reached: 50 receipts or 100 MB. Remove files to add more.'
          )
          break
        }
        created.push({
          id: `local-${Date.now()}-${++fileCounter.current}`,
          merchant: '',
          date: '',
          category: '',
          currency: scope,
          total: null,
          tax: 0,
          taxLabel: 'No tax recorded',
          filename: f.name,
          sample: false,
          type,
          size: f.size,
          url: URL.createObjectURL(f),
          items: [],
        })
        bytes += f.size
      }

      if (created.length) {
        setRecords(prev => [...prev, ...created])
        setSelectedId(created[0].id)
        setFilter('all')
        setSearchInput('')
        requestAnimationFrame(() =>
          workspaceRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        )
      }

      showToast(
        (created.length
          ? `${created.length} file${created.length === 1 ? '' : 's'} added. Select Edit to enter receipt details. `
          : '') + errors.join(' ')
      )
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [records, scope, showToast, undoRecord]
  )

  /* ----------------------------------------------------------------- edit */

  const openEdit = useCallback(() => {
    if (!selected) return
    setEditingId(selected.id)
    setDraft({
      merchant: selected.merchant,
      date: selected.date,
      category: selected.category,
      currency: selected.currency,
      total: selected.total === null ? '' : String(selected.total),
      tax: String(selected.tax ?? 0),
      taxLabel: selected.taxLabel,
      items: selected.items.length
        ? selected.items.map(i => ({
            name: i.name,
            qty: String(i.qty),
            price: String(i.price),
          }))
        : [{ name: '', qty: '1', price: '' }],
    })
    setFormError(null)
    setDialog('edit')
  }, [selected])

  const saveEdit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!draft || !editingId) return
      const base = records.find(r => r.id === editingId)
      if (!base) return

      const items: LineItem[] = draft.items.map(i => ({
        name: i.name.trim(),
        qty: Number(i.qty),
        price: Number(i.price),
      }))
      const updated: Receipt = {
        ...base,
        merchant: draft.merchant.trim(),
        date: draft.date,
        category: draft.category,
        currency: draft.currency,
        total: Number(draft.total),
        tax: Number(draft.tax),
        taxLabel: draft.taxLabel,
        items,
      }

      if (!updated.merchant || items.some(i => !i.name)) {
        setFormError('Enter a merchant and a description for each line item.')
        return
      }
      if (updated.total !== null && updated.tax > updated.total) {
        setFormError('Tax cannot exceed the receipt total.')
        return
      }
      if (
        (updated.total ?? 0) > 999999999 ||
        items.some(i => !Number.isSafeInteger(cents(i.price) * i.qty))
      ) {
        setFormError(
          'Enter a total below 1,000,000,000 and check the item amounts.'
        )
        return
      }

      setRecords(prev => prev.map(x => (x.id === base.id ? updated : x)))
      setDialog(null)
      if (filter !== 'all' && (filter === 'ready') !== ready(updated)) {
        setFilter('all')
      }
      showToast(
        ready(updated)
          ? 'Receipt saved. The numbers match.'
          : 'Receipt saved. Review the flagged details.'
      )
    },
    [draft, editingId, filter, records, showToast]
  )

  /* --------------------------------------------------------------- export */

  const exportCsv = useCallback(() => {
    const rows = visible.filter(r => ready(r))
    if (!rows.length) return
    const headers = [
      'Merchant',
      'Receipt date',
      'Category',
      'Currency',
      'Total',
      'Tax included',
      'Tax label',
      'Line items',
      'Source file',
      'Data source',
    ]
    const lines = rows.map(r => [
      r.merchant,
      r.date,
      r.category,
      r.currency,
      (r.total ?? 0).toFixed(2),
      r.tax.toFixed(2),
      r.taxLabel,
      r.items
        .map(i => `${i.qty} x ${i.name} @ ${i.price.toFixed(2)}`)
        .join('; '),
      r.filename,
      r.sample ? 'Sample' : 'Manual entry',
    ])
    const csv =
      '﻿' +
      [headers, ...lines].map(row => row.map(safeCsv).join(',')).join('\r\n')
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8' })
    )
    const a = document.createElement('a')
    a.href = url
    a.download = `resibo-receipts-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.append(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 30000)
    showToast(
      `${rows.length} reviewed record${rows.length === 1 ? '' : 's'} exported.` +
        (visible.length > rows.length
          ? ' Records needing review were excluded.'
          : '')
    )
  }, [visible, showToast])

  /* ---------------------------------------------------------------- reset */

  const resetSession = useCallback(() => {
    records.forEach(r => {
      if (r.url) URL.revokeObjectURL(r.url)
    })
    if (undoRecord?.record.url) URL.revokeObjectURL(undoRecord.record.url)
    const fresh = cloneSamples()
    setRecords(fresh)
    setSelectedId(fresh[0]?.id ?? null)
    setFilter('all')
    setSearchInput('')
    setCurrencyScope('PHP')
    setUndoRecord(null)
    setDialog(null)
    showToast('Sample workspace restored.')
  }, [records, undoRecord, showToast])

  const removeSelected = useCallback(() => {
    if (!selected) return
    setUndoRecord(prev => {
      // Only one record is undoable at a time, so the outgoing one is gone for
      // good — release its blob instead of stranding it until page teardown.
      if (prev && prev.record.url) URL.revokeObjectURL(prev.record.url)
      return { record: selected, index: records.indexOf(selected) }
    })
    setRecords(prev => prev.filter(x => x.id !== selected.id))
    showToast('Receipt removed from this session.', true)
  }, [selected, records, showToast])

  const undoRemove = useCallback(() => {
    if (!undoRecord) return
    setRecords(prev => {
      const next = [...prev]
      next.splice(undoRecord.index, 0, undoRecord.record)
      return next
    })
    setSelectedId(undoRecord.record.id)
    setUndoRecord(null)
    setFilter('all')
    setSearchInput('')
    showToast('Receipt restored.')
  }, [undoRecord, showToast])

  useEffect(() => {
    if (toast?.undo) return
    setUndoRecord(prev => {
      if (prev?.record.url) URL.revokeObjectURL(prev.record.url)
      return null
    })
  }, [toast])

  const runQuery = useCallback(
    (q: string) => setQueryAnswer(answerQuery(q, records)),
    [records]
  )

  /* --------------------------------------------------------------- render */

  const problems = selected ? issues(selected) : []

  return (
    <div className='rsb-page'>
      <IconSprite />
      <noscript>
        <div className='rsb-load-note'>
          Enable JavaScript to use this receipt workspace. All processing stays
          in this page.
        </div>
      </noscript>

      <div className='rsb-wrap'>
        <section className='rsb-hero' aria-labelledby='rsb-headline'>
          <h1 id='rsb-headline'>
            Every receipt.
            <br />
            Accounted for.
          </h1>
          <div className='rsb-intro'>
            <p>
              Turn a pile of receipts into records you can work with. Review the
              details. Keep the control.
            </p>
            <div className='rsb-flow'>
              <span>01 &nbsp;Add</span>
              <span>/</span>
              <span>02 &nbsp;Review</span>
              <span>/</span>
              <span>03 &nbsp;Export</span>
            </div>
          </div>
        </section>

        <section
          className='rsb-action-grid'
          aria-label='Add receipts and session overview'
        >
          <div
            className={`rsb-upload${dragging ? ' rsb-dragging' : ''}`}
            onDragEnter={e => {
              e.preventDefault()
              dragDepth.current++
              setDragging(true)
            }}
            onDragOver={e => e.preventDefault()}
            onDragLeave={() => {
              dragDepth.current--
              if (dragDepth.current <= 0) setDragging(false)
            }}
            onDrop={e => {
              e.preventDefault()
              dragDepth.current = 0
              setDragging(false)
              addFiles(e.dataTransfer.files)
            }}
          >
            <div className='rsb-upload-top'>
              <h2>
                Drop the
                <br />
                paperwork here.
              </h2>
              <Icon name='upload' className='rsb-upload-icon' />
            </div>
            <p>Preview your files, then enter the receipt details.</p>
            <div className='rsb-upload-bottom'>
              <button
                type='button'
                className='rsb-btn rsb-dark'
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon name='plus' />
                Browse files
              </button>
              <span className='rsb-upload-format'>
                JPG, PNG, WEBP, PDF
                <br />
                10 MB per file · multiple files
              </span>
            </div>
            <input
              ref={fileInputRef}
              type='file'
              aria-label='Add receipt files'
              accept='image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf'
              multiple
              hidden
              onChange={e => e.target.files && addFiles(e.target.files)}
            />
          </div>

          <div className='rsb-overview'>
            <div className='rsb-overview-top'>
              <h2>Session overview</h2>
              <div className='rsb-amount-selector'>
                <label className='rsb-sr-only' htmlFor='rsb-currency-scope'>
                  Currency for overview
                </label>
                <select
                  id='rsb-currency-scope'
                  hidden={currencies.length < 2}
                  value={scope}
                  onChange={e => setCurrencyScope(e.target.value)}
                >
                  {currencies.map(c => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
                <span className='rsb-demo-label'>{datasetLabel}</span>
              </div>
            </div>
            <div className='rsb-metrics'>
              <div className='rsb-metric'>
                <span className='rsb-metric-value'>
                  <small>{scope}</small>
                  <span>{decimal(sessionTotal)}</span>
                </span>
                <div className='rsb-metric-label'>
                  Total recorded{' '}
                  <span>{currencies.length > 1 ? `· ${scope} only` : ''}</span>
                </div>
              </div>
              <div className='rsb-metric'>
                <span className='rsb-metric-value'>{readyCount}</span>
                <div className='rsb-metric-label'>
                  <span className='rsb-dot' />
                  Ready to export
                </div>
              </div>
              <div className='rsb-metric'>
                <span className='rsb-metric-value'>{reviewCount}</span>
                <div className='rsb-metric-label'>
                  <span className='rsb-dot rsb-warning' />
                  Needs review
                </div>
              </div>
            </div>
            <div className='rsb-overview-bottom'>
              <Icon name='file' style={{ width: 13, height: 13 }} />
              <span>{overviewNote}</span>
              <button
                type='button'
                className='rsb-text-button'
                onClick={() => setDialog('help')}
              >
                About this demo
              </button>
            </div>
          </div>
        </section>

        <section
          className='rsb-workspace'
          ref={workspaceRef}
          aria-labelledby='rsb-workspace-heading'
        >
          <div className='rsb-workspace-bar'>
            <div className='rsb-workspace-title'>
              <h2 id='rsb-workspace-heading'>Your receipts</h2>
              <span className='rsb-count'>{records.length}</span>
            </div>
            <div className='rsb-workspace-actions'>
              <button
                type='button'
                className='rsb-btn rsb-ask-btn'
                aria-label='Ask this session'
                onClick={() => setDialog('ask')}
              >
                <Icon name='chat' />
                <span>Ask this session</span>
              </button>
              <button
                type='button'
                className='rsb-btn rsb-light'
                disabled={!eligible.length}
                onClick={exportCsv}
                title={`Export ${eligible.length} ready record${
                  eligible.length === 1 ? '' : 's'
                }${
                  visible.length > eligible.length
                    ? '; review records are excluded'
                    : ''
                }`}
              >
                <Icon name='download' />
                <span>
                  {filter !== 'all' || search
                    ? 'Export filtered CSV'
                    : 'Export CSV'}
                </span>
              </button>
            </div>
          </div>

          <div className='rsb-workbench'>
            <aside className='rsb-receipt-list' aria-label='Receipt list'>
              <div className='rsb-search'>
                <Icon name='search' />
                <label className='rsb-sr-only' htmlFor='rsb-search'>
                  Search merchants, categories or files
                </label>
                <input
                  id='rsb-search'
                  type='search'
                  placeholder='Search receipts'
                  autoComplete='off'
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
              </div>
              <div className='rsb-filters' aria-label='Filter receipts'>
                {(
                  [
                    ['all', 'All', records.length],
                    ['review', 'Review', reviewCount],
                    ['ready', 'Ready', readyCount],
                  ] as Array<[Filter, string, number]>
                ).map(([key, label, count]) => (
                  <button
                    key={key}
                    type='button'
                    className='rsb-filter'
                    aria-pressed={filter === key}
                    onClick={() => setFilter(key)}
                  >
                    {label} <b>{count}</b>
                  </button>
                ))}
              </div>
              <div className='rsb-receipts'>
                {visible.length ? (
                  visible.map(r => (
                    <button
                      key={r.id}
                      type='button'
                      className='rsb-receipt-row'
                      aria-pressed={r.id === activeId}
                      onClick={() => setSelectedId(r.id)}
                    >
                      <div className='rsb-row-top'>
                        <span className='rsb-merchant'>
                          {r.merchant || 'Untitled receipt'}
                        </span>
                        <span className='rsb-row-icon'>
                          <Icon name={r.id === activeId ? 'arrow' : 'file'} />
                        </span>
                      </div>
                      <div className='rsb-row-bottom'>
                        <span className='rsb-row-date'>
                          {shortDate(r.date)} ·{' '}
                          {r.sample ? 'Sample' : 'Your file'}
                        </span>
                        <span className='rsb-row-amount'>
                          {r.total === null ? '—' : money(r.total, r.currency)}
                        </span>
                      </div>
                      {!ready(r) && (
                        <div className='rsb-row-status'>
                          <Icon name='warning' />
                          Needs review
                        </div>
                      )}
                    </button>
                  ))
                ) : (
                  <div className='rsb-empty'>
                    <strong>
                      {records.length
                        ? 'No matching receipts.'
                        : 'A clean slate.'}
                    </strong>
                    {records.length
                      ? 'Try a different search or filter.'
                      : 'Add a receipt to start a record.'}
                    {records.length ? (
                      <>
                        <br />
                        <br />
                        <button
                          type='button'
                          className='rsb-text-button'
                          onClick={() => {
                            setFilter('all')
                            setSearchInput('')
                          }}
                        >
                          Clear filters
                        </button>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
              <div className='rsb-list-bottom'>
                <span>
                  {records.length} receipt{records.length === 1 ? '' : 's'} · in
                  memory
                </span>
                <button
                  type='button'
                  className='rsb-text-button'
                  onClick={() => setDialog('reset')}
                >
                  Reset demo
                </button>
              </div>
            </aside>

            <section className='rsb-source' aria-label='Receipt source'>
              <div className='rsb-pane-head'>
                <span className='rsb-label'>
                  <Icon name='file' />
                  Source receipt
                </span>
                <button
                  type='button'
                  className='rsb-icon-btn'
                  aria-label='Expand receipt preview'
                  disabled={!selected}
                  onClick={() => setDialog('source')}
                >
                  <Icon name='expand' />
                </button>
              </div>
              <div className='rsb-paper-stage'>
                <SourcePreview
                  record={selected}
                  broken={brokenPreviews}
                  onBroken={id => setBrokenPreviews(prev => [...prev, id])}
                />
              </div>
              <div className='rsb-source-caption'>
                <span>{selected?.filename || 'No receipt selected'}</span>
                <span>
                  {selected ? (selected.sample ? 'SAMPLE' : 'LOCAL FILE') : ''}
                </span>
              </div>
            </section>

            <section className='rsb-details' aria-label='Receipt record'>
              <div className='rsb-pane-head'>
                <span className='rsb-label'>Record details</span>
                <span>
                  {selected && (
                    <span
                      className={`rsb-status${problems.length ? ' rsb-review' : ''}`}
                    >
                      <Icon name={problems.length ? 'warning' : 'check'} />
                      {problems.length ? 'Needs review' : 'Ready'}
                    </span>
                  )}
                </span>
              </div>
              <div className='rsb-detail-content'>
                {selected ? (
                  <RecordDetail
                    record={selected}
                    problems={problems}
                    onEdit={openEdit}
                    onRemove={removeSelected}
                  />
                ) : (
                  <div className='rsb-empty'>
                    <strong>Room for the details.</strong>
                    Select a receipt or add a file to get started.
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className='rsb-workspace-foot'>
            <span>
              <Icon name='lock' />
              Your files stay in this page. Refreshing clears the session.
            </span>
            <span>Offline demo · sample records + manual entry</span>
          </div>
        </section>

        <section
          className='rsb-guide'
          aria-label='Three steps to a clean record'
        >
          {[
            [
              '01',
              'Bring the receipts.',
              'Add one file or a whole batch. Preview images and PDFs without sending them anywhere.',
            ],
            [
              '02',
              'Make the numbers match.',
              'Review the merchant, category and line items. See what needs attention before you export.',
            ],
            [
              '03',
              'Close the loop.',
              'Export your reviewed records as a CSV, ready for your spreadsheet and your next step.',
            ],
          ].map(([num, title, body]) => (
            <div className='rsb-guide-step' key={num}>
              <span className='rsb-step-num'>{num}</span>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      {/* ------------------------------------------------------- dialogs */}

      <Dialog
        open={dialog === 'edit'}
        onClose={() => setDialog(null)}
        labelledBy='rsb-edit-title'
      >
        {draft && (
          <form onSubmit={saveEdit}>
            <DialogHead
              id='rsb-edit-title'
              title={
                records.find(r => r.id === editingId)?.total === null
                  ? 'Enter receipt details'
                  : 'Edit receipt'
              }
              closeLabel='Close edit receipt'
              onClose={() => setDialog(null)}
            />
            <div className='rsb-dialog-body'>
              <div className='rsb-form-grid'>
                <label className='rsb-form-field rsb-full'>
                  Merchant
                  <input
                    name='merchant'
                    maxLength={120}
                    required
                    autoComplete='off'
                    value={draft.merchant}
                    onChange={e =>
                      setDraft({ ...draft, merchant: e.target.value })
                    }
                  />
                </label>
                <label className='rsb-form-field'>
                  Receipt date
                  <input
                    name='date'
                    type='date'
                    required
                    value={draft.date}
                    onChange={e => setDraft({ ...draft, date: e.target.value })}
                  />
                </label>
                <label className='rsb-form-field'>
                  Category
                  <select
                    name='category'
                    required
                    value={draft.category}
                    onChange={e =>
                      setDraft({ ...draft, category: e.target.value })
                    }
                  >
                    <option value=''>Choose a category</option>
                    {CATEGORIES.map(c => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label className='rsb-form-field'>
                  Currency
                  <select
                    name='currency'
                    value={draft.currency}
                    onChange={e =>
                      setDraft({ ...draft, currency: e.target.value })
                    }
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className='rsb-form-field'>
                  Receipt total
                  <input
                    name='total'
                    type='number'
                    min='0'
                    step='0.01'
                    required
                    value={draft.total}
                    onChange={e =>
                      setDraft({ ...draft, total: e.target.value })
                    }
                  />
                </label>
                <label className='rsb-form-field'>
                  Tax included in total
                  <input
                    name='tax'
                    type='number'
                    min='0'
                    step='0.01'
                    required
                    value={draft.tax}
                    onChange={e => setDraft({ ...draft, tax: e.target.value })}
                  />
                </label>
                <label className='rsb-form-field'>
                  Tax label
                  <select
                    name='taxLabel'
                    value={draft.taxLabel}
                    onChange={e =>
                      setDraft({ ...draft, taxLabel: e.target.value })
                    }
                  >
                    {TAX_LABELS.map(t => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className='rsb-edit-lines'>
                <div className='rsb-edit-lines-head'>
                  <span>
                    Line items{' '}
                    <span className='rsb-muted'>· amounts include tax</span>
                  </span>
                  <button
                    type='button'
                    className='rsb-text-button'
                    onClick={() =>
                      setDraft({
                        ...draft,
                        items: [
                          ...draft.items,
                          { name: '', qty: '1', price: '' },
                        ],
                      })
                    }
                  >
                    Add item
                  </button>
                </div>
                <div>
                  {draft.items.map((item, idx) => (
                    <div className='rsb-edit-line' key={idx}>
                      <label>
                        Description
                        <input
                          className='rsb-item-name'
                          required
                          maxLength={120}
                          value={item.name}
                          onChange={e => {
                            const items = [...draft.items]
                            items[idx] = { ...item, name: e.target.value }
                            setDraft({ ...draft, items })
                          }}
                        />
                      </label>
                      <label>
                        Qty
                        <input
                          className='rsb-item-qty'
                          type='number'
                          min='1'
                          max='9999'
                          step='1'
                          required
                          value={item.qty}
                          onChange={e => {
                            const items = [...draft.items]
                            items[idx] = { ...item, qty: e.target.value }
                            setDraft({ ...draft, items })
                          }}
                        />
                      </label>
                      <label>
                        Unit price
                        <input
                          className='rsb-item-price'
                          type='number'
                          min='0'
                          max='99999999'
                          step='0.01'
                          required
                          value={item.price}
                          onChange={e => {
                            const items = [...draft.items]
                            items[idx] = { ...item, price: e.target.value }
                            setDraft({ ...draft, items })
                          }}
                        />
                      </label>
                      <button
                        type='button'
                        className='rsb-icon-btn rsb-remove-line'
                        aria-label='Remove line item'
                        onClick={() =>
                          setDraft({
                            ...draft,
                            items: draft.items.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        <Icon name='close' />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <p className='rsb-form-note'>
                Enter the amounts shown on the receipt. Tax is part of the total
                and is not added again. Saving rechecks the line-item sum.
              </p>
              {formError && (
                <p className='rsb-form-error' role='alert'>
                  {formError}
                </p>
              )}
            </div>
            <div className='rsb-dialog-actions'>
              <button
                type='button'
                className='rsb-btn'
                onClick={() => setDialog(null)}
              >
                Cancel
              </button>
              <button type='submit' className='rsb-btn rsb-light'>
                Save receipt
                <Icon name='check' />
              </button>
            </div>
          </form>
        )}
      </Dialog>

      <Dialog
        open={dialog === 'help'}
        onClose={() => setDialog(null)}
        labelledBy='rsb-help-title'
      >
        <DialogHead
          id='rsb-help-title'
          title='Your receipt workspace.'
          closeLabel='Close guide'
          onClose={() => setDialog(null)}
        />
        <div className='rsb-dialog-body'>
          <p>
            A standalone redesign of Resibo by Pixelmojo. Try the whole review
            flow with sample records, or add files of your own.
          </p>
          <div className='rsb-help-list'>
            <div>
              <h3>Start with the samples.</h3>
              <p>
                Select a receipt to compare the source with its record. The
                sample from Common Room has an unassigned category. Edit it to
                resolve the review flag.
              </p>
            </div>
            <div>
              <h3>Your files, your entries.</h3>
              <p>
                This offline edition previews files and lets you enter records
                manually. It does not run AI extraction. The original online
                Resibo demo provides AI receipt extraction.
              </p>
            </div>
            <div>
              <h3>Review, then export.</h3>
              <p>
                Required fields and line-item totals determine whether a record
                is ready. CSV export includes only ready records in your current
                search and status filter. Different currencies stay separate.
              </p>
            </div>
            <div>
              <h3>Private for this session.</h3>
              <p>
                No accounts, network requests or browser storage. Files and
                edits stay in memory and disappear on refresh. Export what you
                need before closing this page.
              </p>
            </div>
          </div>
        </div>
        <div className='rsb-dialog-actions'>
          <button
            type='button'
            className='rsb-btn rsb-light'
            onClick={() => setDialog(null)}
          >
            Back to receipts
          </button>
        </div>
      </Dialog>

      <Dialog
        open={dialog === 'ask'}
        onClose={() => setDialog(null)}
        labelledBy='rsb-ask-title'
      >
        <DialogHead
          id='rsb-ask-title'
          title='Ask this session.'
          closeLabel='Close session query'
          onClose={() => setDialog(null)}
        />
        <div className='rsb-dialog-body'>
          <p>
            Try a local query about totals, categories, merchants or records
            that need review. Answers use the full session. No AI service is
            connected.
          </p>
          <form
            className='rsb-query-box'
            onSubmit={e => {
              e.preventDefault()
              runQuery(query)
            }}
          >
            <label className='rsb-sr-only' htmlFor='rsb-query'>
              Question about receipts
            </label>
            <input
              id='rsb-query'
              placeholder='How much did I spend on transport?'
              required
              maxLength={250}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button type='submit' className='rsb-btn rsb-light'>
              Ask
              <Icon name='arrow' />
            </button>
          </form>
          <div className='rsb-suggestions'>
            {[
              ['What is the total spend?', 'Total spend'],
              ['What needs review?', 'Needs review'],
              ['How much did I spend on meals?', 'Meals & drinks'],
            ].map(([q, label]) => (
              <button
                key={label}
                type='button'
                onClick={() => {
                  setQuery(q)
                  runQuery(q)
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {queryAnswer && (
            <div className='rsb-query-answer' role='status'>
              {queryAnswer}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={dialog === 'source'}
        onClose={() => setDialog(null)}
        className='rsb-source-dialog'
        labelledBy='rsb-source-title'
      >
        <DialogHead
          id='rsb-source-title'
          title={selected?.merchant || selected?.filename || 'Source receipt'}
          closeLabel='Close receipt preview'
          onClose={() => setDialog(null)}
        />
        <div className='rsb-paper-stage'>
          <SourcePreview
            record={selected}
            broken={brokenPreviews}
            onBroken={id => setBrokenPreviews(prev => [...prev, id])}
          />
        </div>
      </Dialog>

      <Dialog
        open={dialog === 'reset'}
        onClose={() => setDialog(null)}
        labelledBy='rsb-reset-title'
      >
        <DialogHead
          id='rsb-reset-title'
          title='Reset this session?'
          closeLabel='Close reset confirmation'
          onClose={() => setDialog(null)}
        />
        <div className='rsb-dialog-body'>
          <p>
            Your current files and edits will be replaced with the three
            original samples. Export any reviewed records you want to keep
            first.
          </p>
        </div>
        <div className='rsb-dialog-actions'>
          <button
            type='button'
            className='rsb-btn'
            onClick={() => setDialog(null)}
          >
            Keep working
          </button>
          <button
            type='button'
            className='rsb-btn rsb-light'
            onClick={resetSession}
          >
            Reset to samples
          </button>
        </div>
      </Dialog>

      {toast && (
        <div className='rsb-toast' role='status'>
          <span>{toast.message}</span>
          {toast.undo && (
            <button type='button' onClick={undoRemove}>
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- fragments */

function PaperReceipt({ record }: { record: Receipt }) {
  return (
    <article
      className='rsb-paper'
      aria-label={`Sample source receipt for ${record.merchant}`}
    >
      <div className='rsb-paper-mark' aria-hidden='true'>
        <i />
        <i />
        <i />
        <i />
      </div>
      <div className='rsb-paper-brand'>{record.merchant}</div>
      <div className='rsb-paper-address'>
        {(record.address ?? '').split('\n').map((line, i) => (
          <span key={i}>
            {i > 0 && <br />}
            {line}
          </span>
        ))}
      </div>
      <div className='rsb-paper-rule' />
      <div className='rsb-paper-row'>
        <span>Receipt</span>
        <span>{record.receiptNumber}</span>
      </div>
      <div className='rsb-paper-row'>
        <span>{longDate(record.date)}</span>
        <span>14:32</span>
      </div>
      <div className='rsb-paper-rule' />
      {record.items.map((i, idx) => (
        <div className='rsb-paper-row' key={idx}>
          <span>
            {i.qty} × {i.name}
          </span>
          <span>{decimal(i.qty * i.price)}</span>
        </div>
      ))}
      <div className='rsb-paper-row rsb-paper-total'>
        <span>Total</span>
        <span>{money(record.total, record.currency)}</span>
      </div>
      <div className='rsb-paper-row'>
        <span>{record.taxLabel}</span>
        <span>{decimal(record.tax)}</span>
      </div>
      <div className='rsb-paper-footer'>
        Thank you. See you again.
        <br />
        Illustrative receipt · sample data only
      </div>
      <div className='rsb-barcode' aria-hidden='true' />
    </article>
  )
}

function SourcePreview({
  record,
  broken,
  onBroken,
}: {
  record: Receipt | null
  broken: string[]
  onBroken: (id: string) => void
}) {
  if (!record) {
    return (
      <div className='rsb-preview-empty'>
        <Icon name='file' />
        <span>Select a receipt to see its source.</span>
      </div>
    )
  }

  if (record.sample) {
    const pristine = sourceCopies.get(record.id) ?? record
    return <PaperReceipt record={pristine} />
  }

  if (record.type === 'application/pdf') {
    return (
      <object
        className='rsb-pdf-preview'
        data={record.url}
        type='application/pdf'
      >
        <div className='rsb-preview-empty'>
          <Icon name='file' />
          <span>This browser cannot display the PDF here.</span>
          <a
            className='rsb-btn'
            href={record.url}
            target='_blank'
            rel='noopener noreferrer'
          >
            Open local PDF
          </a>
        </div>
      </object>
    )
  }

  if (broken.includes(record.id)) {
    return (
      <p className='rsb-preview-empty'>
        This image could not be previewed. Try a different JPG, PNG or WEBP
        file.
      </p>
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className='rsb-source-preview'
      src={record.url}
      alt={`Uploaded receipt: ${record.filename}`}
      onError={() => onBroken(record.id)}
    />
  )
}

function RecordDetail({
  record,
  problems,
  onEdit,
  onRemove,
}: {
  record: Receipt
  problems: string[]
  onEdit: () => void
  onRemove: () => void
}) {
  return (
    <>
      <div className='rsb-detail-title'>
        <div>
          <h3>{record.merchant || 'Untitled receipt'}</h3>
          <p>
            {record.sample ? 'Sample record' : 'Manual record'} ·{' '}
            {record.receiptNumber || 'Details to enter'}
          </p>
        </div>
        <button
          type='button'
          className='rsb-icon-btn'
          aria-label={`Edit ${record.merchant || 'receipt'}`}
          onClick={onEdit}
        >
          <Icon name='edit' />
        </button>
      </div>

      <div className='rsb-fields'>
        <div>
          <div className='rsb-field-name'>Receipt date</div>
          <div className='rsb-field-value rsb-numeric'>
            {longDate(record.date)}
          </div>
        </div>
        <div>
          <div className='rsb-field-name'>Category</div>
          <div
            className='rsb-field-value'
            style={
              !record.category ? { color: 'var(--rsb-warning)' } : undefined
            }
          >
            {record.category || 'Not assigned'}
          </div>
        </div>
        <div>
          <div className='rsb-field-name'>Currency</div>
          <div className='rsb-field-value rsb-numeric'>
            {record.currency}{' '}
            <span className='rsb-muted'>
              {CURRENCY_NAMES[record.currency] || ''}
            </span>
          </div>
        </div>
        <div>
          <div className='rsb-field-name'>{record.taxLabel}</div>
          <div className='rsb-field-value rsb-numeric'>
            {money(record.tax, record.currency)}
          </div>
        </div>
      </div>

      <div className='rsb-detail-rule' />

      <div className='rsb-line-title'>
        Line items
        <span>
          {record.items.length} ITEM{record.items.length === 1 ? '' : 'S'}
        </span>
      </div>

      <table className='rsb-items'>
        <thead>
          <tr>
            <th scope='col'>Description</th>
            <th scope='col'>Qty</th>
            <th scope='col'>Amount</th>
          </tr>
        </thead>
        <tbody>
          {record.items.length ? (
            record.items.map((i, idx) => (
              <tr key={idx}>
                <td>{i.name}</td>
                <td>{i.qty}</td>
                <td>{decimal(i.qty * i.price)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3}>No line items entered</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className='rsb-detail-total'>
        <span>
          Total <span className='rsb-muted'>· {record.currency}</span>
        </span>
        <strong>
          {record.total === null ? '—' : money(record.total, record.currency)}
        </strong>
      </div>

      <div className={`rsb-validation${problems.length ? ' rsb-warning' : ''}`}>
        <Icon name={problems.length ? 'warning' : 'check'} />
        <span>
          {problems.length ? (
            <>
              {problems[0]}
              {problems.length > 1 && (
                <span className='rsb-muted'> +{problems.length - 1} more</span>
              )}
            </>
          ) : (
            'Line items match the total. Ready for export.'
          )}
        </span>
      </div>

      {!record.sample && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 17,
          }}
        >
          <button type='button' className='rsb-text-button' onClick={onEdit}>
            {record.total === null ? 'Enter receipt details' : 'Edit record'}
          </button>
          <button
            type='button'
            className='rsb-text-button rsb-muted'
            style={{ fontSize: 10 }}
            onClick={onRemove}
          >
            Remove receipt
          </button>
        </div>
      )}
    </>
  )
}
